import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
  Alert,
  Spinner,
  Card,
  Row,
  Col,
  Input,
  Label
} from 'reactstrap';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  FaUsers,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaUser,
  FaCode,
  FaBuilding,
  FaBriefcase
} from 'react-icons/fa';
import {
  ApiGetAllConfiguraciones,
  ApiCreateConfiguracion,
  ApiUpdateConfiguracion,
  ApiDeleteConfiguracion
} from '../../../api/navidad';

const ConfiguracionesModal = ({ isOpen, onClose }) => {
  const [configuraciones, setConfiguraciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    usuario: '',
    password: '',
    codigo: '',
    fecha_activacion: '',
    fecha_inactivacion: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadConfiguraciones();
    }
  }, [isOpen]);

  const loadConfiguraciones = async () => {
    setLoading(true);
    try {
      const result = await ApiGetAllConfiguraciones();
      if (result.sms === 'ok') {
        setConfiguraciones(result.data);
      } else {
        toast.error('Error al cargar configuraciones');
      }
    } catch (error) {
      toast.error('Error al cargar configuraciones');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.usuario || !formData.password || !formData.codigo || !formData.fecha_activacion || !formData.fecha_inactivacion) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    setSaving(true);
    try {
      const result = await ApiCreateConfiguracion(formData);
      if (result.sms === 'ok') {
        toast.success('Configuración creada correctamente');
        setShowForm(false);
        setFormData({
          usuario: '',
          password: '',
          codigo: '',
          fecha_activacion: '',
          fecha_inactivacion: ''
        });
        loadConfiguraciones();
      } else {
        toast.error(result.mensaje || 'Error al crear configuración');
      }
    } catch (error) {
      toast.error('Error al crear configuración');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const getEstadoBadge = (config) => {
    const now = new Date();
    const fechaActivacion = new Date(config.fecha_activacion);
    const fechaInactivacion = new Date(config.fecha_inactivacion);

    if (now < fechaActivacion) {
      return <Badge color="info">Pendiente</Badge>;
    } else if (now > fechaInactivacion) {
      return <Badge color="danger">Inactivo</Badge>;
    } else {
      return <Badge color="success">Activo</Badge>;
    }
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} toggle={onClose} size="lg">
        <ModalHeader toggle={onClose}>
          <div className="d-flex align-items-center">
            <FaUsers className="me-2" />
            Configuraciones de Usuarios Encargados
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Cargando configuraciones...</p>
          </div>
        </ModalBody>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        <div className="d-flex align-items-center">
          <FaUsers className="me-2" />
          Configuraciones de Usuarios Encargados
        </div>
      </ModalHeader>
      <ModalBody>
        {!showForm ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Lista de Configuraciones</h6>
              <Button
                color="primary"
                size="sm"
                onClick={() => setShowForm(true)}
              >
                <FaUsers className="me-1" />
                Nueva Configuración
              </Button>
            </div>

            {configuraciones.length === 0 ? (
              <Alert color="info">
                <FaUsers className="me-2" />
                No hay configuraciones registradas
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table hover>
                  <thead className="table-dark">
                    <tr>
                      <th>Usuario</th>
                      <th>Código</th>
                      <th>Estado</th>
                      <th>Período</th>
                      <th>Info Usuario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configuraciones.map((config, index) => (
                      <motion.tr
                        key={config.configuracion_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <td>
                          <div className="d-flex align-items-center">
                            <FaUser className="text-primary me-2" size={14} />
                            <span>{config.usuario}</span>
                          </div>
                        </td>
                        <td>
                          <Badge color="info" className="font-monospace">
                            {config.codigo}
                          </Badge>
                        </td>
                        <td>{getEstadoBadge(config)}</td>
                        <td>
                          <small>
                            <FaCalendarAlt className="me-1" />
                            {new Date(config.fecha_activacion).toLocaleDateString()} - {new Date(config.fecha_inactivacion).toLocaleDateString()}
                          </small>
                        </td>
                        <td>
                          {config.user_info_parsed ? (
                            <div>
                              <div className="d-flex align-items-center">
                                <FaBuilding className="text-muted me-1" size={10} />
                                <small>{config.user_info_parsed.area || '-'}</small>
                              </div>
                              <div className="d-flex align-items-center">
                                <FaBriefcase className="text-muted me-1" size={10} />
                                <small>{config.user_info_parsed.cargo || '-'}</small>
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        ) : (
          <Card>
            <div className="card-header">
              <h6 className="mb-0">Nueva Configuración</h6>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label">Usuario *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.usuario}
                        onChange={(e) => setFormData({...formData, usuario: e.target.value})}
                        required
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label">Contraseña *</label>
                      <input
                        type="password"
                        className="form-control"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        required
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label">Código *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.codigo}
                        onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                        required
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label">Fecha de Activación *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.fecha_activacion}
                        onChange={(e) => setFormData({...formData, fecha_activacion: e.target.value})}
                        required
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label">Fecha de Inactivación *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.fecha_inactivacion}
                        onChange={(e) => setFormData({...formData, fecha_inactivacion: e.target.value})}
                        required
                      />
                    </div>
                  </Col>
                </Row>
                <div className="d-flex gap-2">
                  <Button
                    type="submit"
                    color="primary"
                    disabled={saving}
                    className="d-flex align-items-center"
                  >
                    {saving ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="me-2" />
                        Guardar
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    color="outline-secondary"
                    onClick={() => setShowForm(false)}
                    disabled={saving}
                  >
                    <FaTimesCircle className="me-2" />
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ConfiguracionesModal;
